/**
 * Referrals Service
 * Handles referral code generation, tracking, and rewards
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { getSupabaseErrorMessage } from '@/lib/errorHandler';

export interface Referral {
  id: string;
  code: string;
  referrer_user_id: string;
  referred_user_id: string | null;
  converted_at: string | null;
  reward_status: 'pending' | 'claimed' | 'expired';
  created_at: string;
  updated_at: string;
}

export class ReferralsService {
  /**
   * Get or create referral code for current user
   * Uses auth.uid() internally, no parameters needed
   */
  static async getOrCreateReferralCode(): Promise<string> {
    try {
      const { data, error } = await supabase.rpc('get_or_create_referral_code');

      if (error) {
        logger.error('[ReferralsService] Error getting referral code', error);
        throw new Error(getSupabaseErrorMessage(error));
      }

      return data as string;
    } catch (error: any) {
      logger.error('[ReferralsService] Failed to get referral code', error);
      throw error;
    }
  }

  /**
   * Get referral stats for current user
   */
  static async getReferralStats(userId: string): Promise<{
    total_referrals: number;
    converted: number;
    pending: number;
    reward_status: string;
  }> {
    try {
      const { data, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('referrer_user_id', userId);

      if (error) {
        logger.error('[ReferralsService] Error fetching referral stats', error);
        throw new Error(getSupabaseErrorMessage(error));
      }

      const referrals = (data || []) as Referral[];
      
      return {
        total_referrals: referrals.length,
        converted: referrals.filter(r => r.converted_at !== null).length,
        pending: referrals.filter(r => r.reward_status === 'pending').length,
        reward_status: referrals[0]?.reward_status || 'pending',
      };
    } catch (error: any) {
      logger.error('[ReferralsService] Failed to get referral stats', error);
      return {
        total_referrals: 0,
        converted: 0,
        pending: 0,
        reward_status: 'pending',
      };
    }
  }

  /**
   * Get referral by code (for signup flow)
   */
  static async getReferralByCode(code: string): Promise<Referral | null> {
    try {
      const { data, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('code', code.toUpperCase())
        .maybeSingle();

      if (error) {
        logger.error('[ReferralsService] Error fetching referral by code', error);
        return null;
      }

      return data as Referral | null;
    } catch (error: any) {
      logger.error('[ReferralsService] Failed to get referral by code', error);
      return null;
    }
  }

  /**
   * Link referred user to referral code (called during signup)
   */
  static async linkReferredUser(code: string, referredUserId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('referrals')
        .update({
          referred_user_id: referredUserId,
          updated_at: new Date().toISOString(),
        })
        .eq('code', code.toUpperCase());

      if (error) {
        logger.error('[ReferralsService] Error linking referred user', error);
        throw new Error(getSupabaseErrorMessage(error));
      }
    } catch (error: any) {
      logger.error('[ReferralsService] Failed to link referred user', error);
      throw error;
    }
  }

  /**
   * Mark referral as converted (when referred user pays/subscribes)
   */
  static async markAsConverted(code: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('referrals')
        .update({
          converted_at: new Date().toISOString(),
          reward_status: 'claimed',
          updated_at: new Date().toISOString(),
        })
        .eq('code', code.toUpperCase());

      if (error) {
        logger.error('[ReferralsService] Error marking referral as converted', error);
        throw new Error(getSupabaseErrorMessage(error));
      }
    } catch (error: any) {
      logger.error('[ReferralsService] Failed to mark referral as converted', error);
      throw error;
    }
  }
}

